import {loadStripe} from '@stripe/stripe-js';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
    useLazyFetchStripeClientSecretQuery,
    useLazyFetchStripePaymentSummaryQuery,
} from 'components/customer/Confirmation/store/gatewayApi';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {Elements} from '@stripe/react-stripe-js';
import {genericMessageHandler, Icon, Loader} from 'shared/helpers';
import {useTheme} from 'styled-components';
import {PaymentForm} from 'components/customer/Confirmation/Gateways/Stripe/PaymentForm';
import {Container as MessageContainer} from 'components/customer/Confirmation/JobConfirmation';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    clientSecretSet,
    connectedAccountIdSet,
    paymentIntentIdSet,
    selectClientSecret,
    selectConfirmationToken,
    selectPaymentIntentId,
    selectPaymentMethod,
    selectPaymentType,
    updatingSessionSet,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {useNotificationContext} from 'contexts';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {useUpdatePaymentIntent} from 'components/customer/Confirmation/helpers/updatePaymentIntent';
import {useGetConfigurationQuery} from 'store/customer/appSlice';

interface StripeProps {
    publishableKey: string;
}

export const Stripe = ({publishableKey}: StripeProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const {userProfile} = useAppContext();
    const {notify} = useNotificationContext();
    const {job} = useJobContext() as PartialJob;

    const selectedPaymentMethod = useAppSelector(selectPaymentMethod);
    const selectedPaymentType = useAppSelector(selectPaymentType);

    const clientSecret = useAppSelector(selectClientSecret);
    const confirmationToken = useAppSelector(selectConfirmationToken);
    const paymentIntentId = useAppSelector(selectPaymentIntentId);

    const {updateIntent} = useUpdatePaymentIntent();
    const [updatePaymentIntent] = useLazyFetchStripePaymentSummaryQuery();
    const [
        getClientSecret,
        {data, isError, isLoading, isUninitialized, isFetching},
    ] = useLazyFetchStripeClientSecretQuery();
    const {data: configuration, isLoading: isConfigurationLoading} =
        useGetConfigurationQuery();

    const activeRequests = useRef(0);
    const updatePaymentSession = useCallback(async () => {
        if (selectedPaymentType == PaymentType.GATEWAY) {
            if (confirmationToken && paymentIntentId) {
                activeRequests.current++;
                dispatch(updatingSessionSet(true));
                try {
                    const {isError} = await updatePaymentIntent({
                        confirmationToken,
                        paymentIntentId,
                        manufacturerId: userProfile?.manufacturerId,
                        paymentMethod: selectedPaymentMethod,
                        jobId: job.id,
                    });

                    let updateResponse;
                    if (!isError) {
                        updateResponse = await updateIntent(
                            selectedPaymentMethod,
                            job.id,
                            userProfile.manufacturerId
                        );
                    }

                    if (isError || (updateResponse && updateResponse?.error)) {
                        throw new Error(
                            'An error occurred while updating the payment session. Please try again later.'
                        );
                    }
                } catch (e) {
                    genericMessageHandler(notify, e);
                } finally {
                    activeRequests.current--;

                    if (activeRequests.current == 0) {
                        dispatch(updatingSessionSet(false));
                    }
                }
            }
        }
    }, [selectedPaymentMethod, userProfile, selectedPaymentType, job]);

    useEffect(() => {
        if (data) {
            dispatch(clientSecretSet(data.clientSecret));
            dispatch(connectedAccountIdSet(data.connectedAccountId));
            dispatch(paymentIntentIdSet(data.paymentIntentId));
        }
    }, [data]);

    useEffect(() => {
        if (!clientSecret) {
            if (job && selectedPaymentMethod) {
                void getClientSecret({
                    jobID: job.id,
                    paymentType: selectedPaymentMethod,
                });
            }
        }
    }, [clientSecret, job, selectedPaymentMethod]);

    useEffect(() => {
        void updatePaymentSession();
    }, [updatePaymentSession]);

    useEffect(() => {
        return () => {
            dispatch(clientSecretSet(undefined));
        };
    }, []);

    const stripe = useMemo(() => {
        if (publishableKey && configuration) {
            return loadStripe(publishableKey, {
                developerTools: {
                    assistant: {
                        // production has environment name as empty string and other env
                        // has specific name like local dev, staging, test1 etc.
                        enabled: configuration.environmentName != '',
                    },
                },
            });
        }
    }, [publishableKey, configuration]);

    if (isError) {
        return (
            <MessageContainer>
                <Icon iconName="product_not_found.svg" />
                <h1>Error!</h1>
                <div>Something went wrong! Please try again later.</div>
            </MessageContainer>
        );
    }

    if (
        !clientSecret ||
        isLoading ||
        isUninitialized ||
        isFetching ||
        isConfigurationLoading ||
        stripe == undefined
    ) {
        return <Loader loader={true} />;
    }

    return (
        <Elements
            key={clientSecret}
            stripe={stripe}
            options={{
                clientSecret,
                appearance: {
                    theme: 'flat',
                    labels: 'floating',
                    variables: {
                        colorText: '#6d6e78',
                        colorTextSecondary: '#6d6e78',
                        colorPrimary: theme.colors.primary.main,
                        iconColor: theme.colors.primary.main,
                        borderRadius: '5px',
                    },
                    rules: {
                        '.Label': {
                            display: 'none', // This hides the card header/label
                        },
                    },
                },
            }}>
            <PaymentForm
                connectedAccountId={data?.connectedAccountId}
                paymentIntentId={data?.paymentIntentId}
            />
        </Elements>
    );
};
